// Per-course copy, keyed by the slugs in app/data/courses.ts.
const en = {
  badgeNew: "New",

  ai: {
    title: "AI & Machine Learning",
    short:
      "Build intelligent apps, from machine learning foundations to LLMs and AI agents.",
    long: "Start with the Python and data handling the field runs on, then work through machine learning foundations: how a model learns from data, why it overfits, and how to tell a good result from a lucky one. From there you move into neural networks and deep learning, and then into the tools reshaping the industry right now, including large language models, prompt engineering, retrieval, and AI agents that call real tools. You finish by building and deploying an application of your own. No mathematics beyond secondary school is assumed, and every session is hands-on with an instructor working alongside you.",
    level: "Beginner to Advanced",
    duration: "10-14 weeks",
    topics: [
      "Python for AI and data",
      "Machine learning foundations",
      "Neural networks and deep learning",
      "Large language models (LLMs)",
      "Prompt engineering",
      "Building AI agents and apps",
    ],
  },

  algorithms: {
    title: "Algorithms",
    short:
      "Design and implement algorithms to solve complex problems efficiently.",
    long: "Learn to reason about what a program costs before you run it. The course starts with sorting, searching and Big-O notation, then works up through recursion, backtracking, dynamic programming, greedy methods and graph theory. Each topic is taught by writing the algorithm yourself and then breaking it, so you understand where it fails and why the standard library implementation looks the way it does. This is the course that makes technical interviews tractable, and the one that separates people who can write code from people who can tell whether that code will hold up under load.",
    level: "Beginner to Advanced",
    duration: "8-12 weeks",
    topics: [
      "Sorting and searching",
      "Complexity and Big-O",
      "Recursion and backtracking",
      "Dynamic programming",
      "Graph theory",
      "Greedy algorithms",
    ],
  },

  programming: {
    title: "Fundamental programming",
    short:
      "Build a solid foundation in coding, problem-solving and clean code.",
    long: "The starting point if you have never written a line of code. You begin with variables, types, control flow and loops, then move on to functions, scope, and structuring a program so it stays readable as it grows. Along the way you meet object-oriented programming, error handling, and the habits that separate code that merely runs from code someone else can maintain. The emphasis throughout is on problem-solving rather than syntax memorisation: by the end you can take a problem stated in plain language and turn it into a working program on your own.",
    level: "Beginner",
    duration: "6-10 weeks",
    topics: [
      "Variables and data types",
      "Control flow and loops",
      "Functions and scope",
      "Object-oriented programming",
      "Error handling",
      "Clean code basics",
    ],
  },

  architecture: {
    title: "Software Design & Architecture",
    short: "Principles for scalable, maintainable software systems.",
    long: "For developers who can already build features and now want to design systems. The course covers the SOLID principles and the design patterns worth knowing, then moves into system design proper: how to decompose a domain, where to draw service boundaries, when microservices help and when they are an expensive mistake. You work through scalability, caching, performance and failure modes on real architectures, and you practise writing the technical documents that get a design approved. Expect to spend as much time defending your decisions as making them.",
    level: "Intermediate to Advanced",
    duration: "8-12 weeks",
    topics: [
      "SOLID principles",
      "Design patterns",
      "System design",
      "Scalability and performance",
      "Microservices",
      "Technical documentation",
    ],
  },

  web: {
    title: "Web development",
    short: "Build modern web apps, front-end through back-end.",
    long: "Build web applications from the browser all the way to the database. You start with semantic HTML and modern CSS layout, then JavaScript properly rather than as a collection of snippets, then React and how a component tree actually renders. The back half covers Node.js, designing an API, talking to a database, authentication, and deploying the result so other people can use it. You leave with a full-stack application you built yourself and an understanding of what happens between typing a URL and seeing a page.",
    level: "Beginner to Advanced",
    duration: "10-14 weeks",
    topics: [
      "HTML and CSS",
      "JavaScript",
      "React",
      "Node.js and APIs",
      "Databases",
      "Deployment",
    ],
  },

  mobile: {
    title: "Mobile development",
    short: "Ship apps for iOS and Android, native and cross-platform.",
    long: "Ship applications to both major mobile platforms. You work natively first, with Swift on iOS and Kotlin on Android, so you understand each platform's conventions rather than fighting them through an abstraction. Then you build the same application cross-platform in Flutter and React Native and see exactly what each approach costs you. The course covers state management, local storage, working with device APIs, and the parts nobody teaches: app store review, versioning, and shipping an update without breaking the people already using it.",
    level: "Intermediate",
    duration: "10-14 weeks",
    topics: [
      "Swift (iOS)",
      "Kotlin (Android)",
      "Flutter",
      "React Native",
      "State and storage",
      "Publishing to stores",
    ],
  },

  server: {
    title: "Database & Server development",
    short: "Design and run databases, APIs and deployments.",
    long: "Everything that happens after the request leaves the browser. You learn to model data properly in SQL, when a document or key-value store is the better answer, and how to read a query plan when something is slow. From there the course covers server-side programming, designing REST and GraphQL APIs, authentication and the security mistakes that make headlines, then caching and background queues for when a single request is doing too much. It ends with deployment, monitoring and the operational side of keeping a service running once real users depend on it.",
    level: "Intermediate",
    duration: "8-12 weeks",
    topics: [
      "SQL and NoSQL",
      "Server-side programming",
      "REST and GraphQL APIs",
      "Auth and security",
      "Caching and queues",
      "Deployment and DevOps",
    ],
  },

  career: {
    title: "CV & Job Search",
    short:
      "Write a CV that gets interviews and run a job search that ends in an offer.",
    long: "Getting hired is a skill of its own, separate from being able to do the job. This course starts with how hiring actually works: who reads your CV, for how long, and what the screening software discards before a human sees anything. You rewrite your CV and your LinkedIn and GitHub profiles against that reality, build a portfolio that proves what you claim, and learn to find openings beyond the job boards everyone else is refreshing. Then you practise the rest of the pipeline: applications, interviews with mock sessions and honest feedback, and negotiating an offer without talking yourself down. Materials cover the German and wider European market as well as applying internationally.",
    level: "All levels",
    duration: "4-6 weeks",
    topics: [
      "CV writing and structure",
      "LinkedIn and GitHub profiles",
      "Building a portfolio",
      "Finding openings and applying",
      "Interview practice",
      "Salary negotiation",
    ],
  },
};

export default en;
